import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import AdminSidebar from '../../components/ui/AdminSidebar';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
// Data will come from Supabase
const affiliatesData = { affiliates: [] };

const AffiliateRegistrationForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [isLoading, setSaving] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cpfCnpj: '',
    commissionType: 'percentage',
    commissionValue: 15,
    products: [],
    status: 'ativo',
    bankAccount: '',
    bankAgency: '',
    bankNumber: '',
    accountType: 'corrente',
    pixKey: '',
    observations: ''
  });

  const [errors, setErrors] = useState({});
  const [completionProgress, setCompletionProgress] = useState(0);

  const breadcrumbItems = [
    { label: 'Admin', path: '/admin-panel-dashboard' },
    { label: 'Afiliados', path: '/admin/affiliates' },
    { label: isEditMode ? 'Editar Afiliado' : 'Novo Afiliado', path: '', isActive: true }
  ];

  const tabs = [
    { id: 'personal', label: 'Informações Pessoais', icon: 'User' },
    { id: 'banking', label: 'Dados Bancários', icon: 'CreditCard' },
    { id: 'commission', label: 'Configurações de Comissão', icon: 'Percent' },
    { id: 'documents', label: 'Documentos', icon: 'FileText' }
  ];

  const mockProducts = [
    { id: 1, name: 'Curso Completo de Marketing Digital' },
    { id: 2, name: 'Bootcamp de Programação' },
    { id: 3, name: 'Mentoria Individual' },
    { id: 4, name: 'Consultoria Empresarial' }
  ];

  // Load existing affiliate data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      const affiliate = affiliatesData?.affiliates?.find(a => a?.id === parseInt(id));
      if (affiliate) {
        setFormData({
          name: affiliate?.name || '',
          email: affiliate?.email || '',
          phone: affiliate?.phone || '',
          cpfCnpj: affiliate?.cpfCnpj || '',
          commissionType: affiliate?.commissionType || 'percentage',
          commissionValue: affiliate?.commissionValue || 15,
          products: affiliate?.products || [],
          status: affiliate?.status || 'ativo',
          bankAccount: affiliate?.bankAccount || '',
          bankAgency: affiliate?.bankAgency || '',
          bankNumber: affiliate?.bankNumber || '',
          accountType: affiliate?.accountType || 'corrente',
          pixKey: affiliate?.pixKey || '',
          observations: affiliate?.observations || ''
        });
      }
    }
  }, [isEditMode, id]);

  // Auto-save functionality
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData?.name || formData?.email) {
        localStorage.setItem('affiliateFormDraft', JSON.stringify(formData));
        setAutoSaveStatus('Rascunho salvo automaticamente');
        setTimeout(() => setAutoSaveStatus(''), 2000);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [formData]);

  // Calculate completion progress
  useEffect(() => {
    const requiredFields = ['name', 'email', 'commissionValue'];
    const optionalFields = ['phone', 'cpfCnpj', 'bankAccount', 'products'];
    const allFields = [...requiredFields, ...optionalFields];
    
    const completedFields = allFields?.filter(field => {
      const value = formData?.[field];
      return value && (Array.isArray(value) ? value?.length > 0 : String(value)?.trim()?.length > 0);
    });
    
    setCompletionProgress(Math.round((completedFields?.length / allFields?.length) * 100));
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) newErrors.name = 'Nome é obrigatório';
    if (!formData?.email?.trim()) newErrors.email = 'E-mail é obrigatório';
    if (formData?.email && !/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'E-mail inválido';
    }
    if (!formData?.commissionValue || formData?.commissionValue <= 0) {
      newErrors.commissionValue = 'Valor da comissão deve ser maior que zero';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleProductToggle = (productId) => {
    const products = formData?.products || [];
    const newProducts = products?.includes(productId)
      ? products?.filter(id => id !== productId)
      : [...products, productId];
    handleInputChange('products', newProducts);
  };

  const handleSaveDraft = () => {
    localStorage.setItem('affiliateFormDraft', JSON.stringify(formData));
    showToast('Rascunho salvo com sucesso!', 'success');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      showToast('Por favor, corrija os erros no formulário', 'error');
      return;
    }

    setSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const newAffiliate = {
        id: isEditMode ? parseInt(id) : Date.now(),
        ...formData,
        code: isEditMode ? formData?.code : generateAffiliateCode(formData?.name),
        link: isEditMode ? formData?.link : generateAffiliateLink(formData?.name),
        referrals: isEditMode ? formData?.referrals || 0 : 0,
        commissionPending: isEditMode ? formData?.commissionPending || 0 : 0,
        commissionPaid: isEditMode ? formData?.commissionPaid || 0 : 0,
        totalCommission: isEditMode ? formData?.totalCommission || 0 : 0,
        lastActivity: new Date()?.toISOString(),
        joinedAt: isEditMode ? formData?.joinedAt : new Date()?.toISOString()
      };

      // Update local JSON data (mock persistence)
      if (isEditMode) {
        const updatedAffiliates = affiliatesData?.affiliates?.map(affiliate =>
          affiliate?.id === parseInt(id) ? newAffiliate : affiliate
        );
        // In real implementation, this would update the actual JSON file
        console.log('Updated affiliate:', newAffiliate);
      } else {
        affiliatesData?.affiliates?.push(newAffiliate);
        console.log('Created affiliate:', newAffiliate);
      }

      // Clear draft
      localStorage.removeItem('affiliateFormDraft');
      
      showToast(`Afiliado ${isEditMode ? 'atualizado' : 'criado'} com sucesso!`, 'success');
      navigate('/admin/affiliates');
    } catch (error) {
      showToast('Erro ao salvar afiliado. Tente novamente.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const generateAffiliateCode = (name) => {
    const initials = name?.trim()?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase();
    const number = Math.floor(Math.random() * 1000)?.toString()?.padStart(3, '0');
    return `${initials}${number}`;
  };

  const generateAffiliateLink = (name) => {
    const code = generateAffiliateCode(name);
    return `https://academy.majorhigh.com/ref/${code}`;
  };

  const showToast = (message, type) => {
    // Mock toast notification
    const toastElement = document.createElement('div');
    toastElement.className = `fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg ${
      type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
    }`;
    toastElement.textContent = message;
    document.body?.appendChild(toastElement);
    
    setTimeout(() => {
      document.body?.removeChild(toastElement);
    }, 3000);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Nome Completo *"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                placeholder="Digite o nome completo"
              />
              <Input
                label="E-mail *"
                type="email"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                error={errors?.email}
                placeholder="Digite o e-mail"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Telefone"
                value={formData?.phone}
                onChange={(e) => handleInputChange('phone', e?.target?.value)}
                placeholder="(00) 00000-0000"
              />
              <Input
                label="CPF/CNPJ"
                value={formData?.cpfCnpj}
                onChange={(e) => handleInputChange('cpfCnpj', e?.target?.value)}
                placeholder="000.000.000-00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <Select
                value={formData?.status}
                onValueChange={(value) => handleInputChange('status', value)}
                placeholder="Selecione o status"
              >
                <option value="ativo">Ativo</option>
                <option value="inativo">Inativo</option>
                <option value="pendente">Pendente</option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Observações
              </label>
              <textarea
                value={formData?.observations}
                onChange={(e) => handleInputChange('observations', e?.target?.value)}
                placeholder="Observações sobre o afiliado..."
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-background"
              />
            </div>
          </div>
        );

      case 'banking':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Input
                label="Banco"
                value={formData?.bankNumber}
                onChange={(e) => handleInputChange('bankNumber', e?.target?.value)}
                placeholder="000"
              />
              <Input
                label="Agência"
                value={formData?.bankAgency}
                onChange={(e) => handleInputChange('bankAgency', e?.target?.value)}
                placeholder="0000"
              />
              <Input
                label="Conta"
                value={formData?.bankAccount}
                onChange={(e) => handleInputChange('bankAccount', e?.target?.value)}
                placeholder="00000-0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Tipo de Conta
              </label>
              <Select
                value={formData?.accountType}
                onValueChange={(value) => handleInputChange('accountType', value)}
                placeholder="Selecione o tipo de conta"
              >
                <option value="corrente">Conta Corrente</option>
                <option value="poupanca">Poupança</option>
              </Select>
            </div>

            <Input
              label="Chave PIX"
              value={formData?.pixKey}
              onChange={(e) => handleInputChange('pixKey', e?.target?.value)}
              placeholder="Digite a chave PIX"
            />

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Icon name="Shield" size={20} className="text-blue-600 mr-3 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-blue-900 mb-1">
                    Dados Protegidos
                  </h3>
                  <p className="text-sm text-blue-700">
                    Todas as informações bancárias são criptografadas e armazenadas com segurança.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'commission':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Tipo de Comissão
                </label>
                <Select
                  value={formData?.commissionType}
                  onValueChange={(value) => handleInputChange('commissionType', value)}
                  placeholder="Selecione o tipo"
                >
                  <option value="percentage">Percentual</option>
                  <option value="fixed">Valor Fixo</option>
                </Select>
              </div>

              <Input
                label={`Valor da Comissão ${formData?.commissionType === 'percentage' ? '(%)' : '(R$)'} *`}
                type="number"
                value={formData?.commissionValue}
                onChange={(e) => handleInputChange('commissionValue', parseFloat(e?.target?.value) || 0)}
                error={errors?.commissionValue}
                placeholder={formData?.commissionType === 'percentage' ? '15' : '100.00'}
                min="0"
                step={formData?.commissionType === 'percentage' ? '0.1' : '0.01'}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Produtos/Serviços Associados
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {mockProducts?.map((product) => (
                  <label key={product?.id} className="flex items-center space-x-3 p-3 border border-border rounded-lg cursor-pointer hover:bg-accent/50">
                    <input
                      type="checkbox"
                      checked={formData?.products?.includes(product?.id)}
                      onChange={() => handleProductToggle(product?.id)}
                      className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                    />
                    <span className="text-sm text-foreground">{product?.name}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* Commission Preview */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-green-900 mb-2">
                Preview de Comissão
              </h3>
              <div className="space-y-2 text-sm text-green-700">
                <p>
                  • Venda de R$ 100,00 = Comissão de {formData?.commissionType === 'percentage' 
                    ? `R$ ${((100 * formData?.commissionValue) / 100)?.toFixed(2)}`
                    : `R$ ${formData?.commissionValue?.toFixed(2)}`}
                </p>
                <p>
                  • Venda de R$ 500,00 = Comissão de {formData?.commissionType === 'percentage'
                    ? `R$ ${((500 * formData?.commissionValue) / 100)?.toFixed(2)}`
                    : `R$ ${formData?.commissionValue?.toFixed(2)}`}
                </p>
                <p>
                  • Venda de R$ 1.000,00 = Comissão de {formData?.commissionType === 'percentage'
                    ? `R$ ${((1000 * formData?.commissionValue) / 100)?.toFixed(2)}`
                    : `R$ ${formData?.commissionValue?.toFixed(2)}`}
                </p>
              </div>
            </div>
          </div>
        );

      case 'documents':
        return (
          <div className="space-y-6">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                Upload de Documentos
              </h3>
              <p className="text-muted-foreground mb-4">
                Arraste e solte arquivos ou clique para selecionar
              </p>
              <Button variant="outline" iconName="Upload" iconPosition="left">
                Selecionar Arquivos
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Aceita: PDF, JPG, PNG (máx. 5MB por arquivo)
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-foreground">Documentos Necessários:</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center">
                  <Icon name="FileText" size={16} className="mr-2" />
                  RG ou CNH (documento com foto)
                </li>
                <li className="flex items-center">
                  <Icon name="FileText" size={16} className="mr-2" />
                  CPF ou CNPJ
                </li>
                <li className="flex items-center">
                  <Icon name="FileText" size={16} className="mr-2" />
                  Comprovante de endereço
                </li>
                <li className="flex items-center">
                  <Icon name="FileText" size={16} className="mr-2" />
                  Contrato de afiliado assinado
                </li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="page-affiliate-form">
      <Header />
      <AdminSidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'ml-16' : 'ml-72'
      }`}>
        <div className="p-6">
          <Breadcrumb customItems={breadcrumbItems} />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                {isEditMode ? 'Editar Afiliado' : 'Novo Afiliado'}
              </h1>
              <p className="text-muted-foreground font-caption">
                {isEditMode ? 'Atualize as informações do afiliado' : 'Adicione um novo parceiro ao programa'}
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              {autoSaveStatus && (
                <span className="text-sm text-green-600 flex items-center">
                  <Icon name="Check" size={14} className="mr-1" />
                  {autoSaveStatus}
                </span>
              )}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Progresso:</span>
                <div className="w-20 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all duration-300"
                    style={{ width: `${completionProgress}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-foreground">{completionProgress}%</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="max-w-4xl">
            {/* Tab Navigation */}
            <div className="bg-card rounded-lg border border-border mb-6">
              <div className="border-b border-border">
                <nav className="flex space-x-8 px-6" aria-label="Tabs">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      type="button"
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                      }`}
                    >
                      <Icon name={tab?.icon} size={16} className="mr-2" />
                      {tab?.label}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {renderTabContent()}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-between bg-card rounded-lg border border-border p-6">
              <div className="flex items-center space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveDraft}
                  iconName="Save"
                  iconPosition="left"
                >
                  Salvar Rascunho
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/admin/affiliates')}
                >
                  Cancelar
                </Button>
              </div>

              <Button
                type="submit"
                loading={isLoading}
                iconName="UserPlus"
                iconPosition="left"
                data-testid="btn-create-affiliate"
                className="bg-yellow-500 hover:bg-yellow-600"
              >
                {isEditMode ? 'Atualizar Afiliado' : 'Criar Afiliado'}
              </Button>
            </div>
          </form>
        </div>
      </main>
      <FloatingAIChat />
    </div>
  );
};

export default AffiliateRegistrationForm;